// PurchasePopup.jsx
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import emailjs from "emailjs-com";
import { useLanguage } from "./LanguageProvider";

const PORTAL_ID = "purchase-portal-root";

function ensurePortalRoot() {
  let el = document.getElementById(PORTAL_ID);
  if (!el) {
    el = document.createElement("div");
    el.id = PORTAL_ID;
    document.body.appendChild(el);
  }
  return el;
}

export default function PurchasePopup({
  open = false,
  product = "Candy Bot Pro",
  onClose,
  onRegister,
  // ✅ copy arriva dal parent: es. copy={{ compareSection: compareCopy }}
  // oppure copy={{ en:{...}, it:{...} }} ecc.
  copy = null,
  // ✅ emailJs NON deve contenere "copy"
  emailJs = {
    service: "service_yekp18n",
    template: "template_6jy0au8",
    publicKey: "qcGu9Ew-DYw_l-ScC",
  },
}) {
  const { language, t } = useLanguage();

  // ✅ Safe getter che cerca prima nel copy passato dal parent, poi nel provider
  // Supporta:
  // - copy semplice: { compareSection: {...} }
  // - copy multilingua: { en:{...}, it:{...} } (prende copy[language])
  const S = (keys, fallback) => {
    if (!Array.isArray(keys)) keys = [keys];

    for (const path of keys) {
      const seg = String(path).split(".");
      let cur = (copy?.[language] ?? copy) ?? t?.[language];

      for (const k of seg) {
        if (cur == null) break;
        cur = cur[k];
      }

      // ✅ non usare "if(cur)" perché 0/false/stringa vuota sono validi
      if (cur !== undefined && cur !== null) return cur;
    }
    return fallback ?? "";
  };

  const [phase, setPhase] = useState("form"); // form | loader | success
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    password: "",
    confirm: "",
  });

  const formRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const style = document.createElement("style");
    style.id = "purchase-anti-zoom";
    style.textContent = `
      input,select,textarea,button{font-size:16px !important; touch-action:manipulation;}
      html{-webkit-text-size-adjust:100%}
      @keyframes dotPulse{0%{transform:scale(1);opacity:.35}50%{transform:scale(1.25);opacity:1}100%{transform:scale(1);opacity:.35}}
    `;
    document.head.appendChild(style);

    return () => {
      document.body.style.overflow = prev;
      document.getElementById("purchase-anti-zoom")?.remove();
    };
  }, [open]);

  if (!open) return null;
  const portalRoot = ensurePortalRoot();

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const splitName = (full) => {
    const parts = (full || "").trim().split(/\s+/);
    const firstName = parts.shift() || "";
    const lastName = parts.length ? parts.join(" ") : "";
    return { firstName, lastName };
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");

    if (typeof onRegister !== "function") {
      console.error("PurchasePopup: onRegister non passato o non è una funzione");
      setErr("Errore interno: registrazione non configurata.");
      return;
    }

    const { fullName, email, password, confirm } = form;

    if (!fullName || !email || !password) {
      setErr(
        S(
          [
            "compareSection.form.fillAll",
            "compareSection.form.fullName",
            "compareSection.form.name",
          ],
          "Compila tutti i campi."
        )
      );
      return;
    }

    if (password.length < 6) {
      setErr(
        S(
          ["compareSection.form.passwordMin", "form.passwordMin"],
          "La password deve avere almeno 6 caratteri."
        )
      );
      return;
    }

    if (password !== confirm) {
      setErr(
        S(
          ["compareSection.form.passwordMismatch", "form.passwordMismatch"],
          "Le password non coincidono."
        )
      );
      return;
    }

    document.activeElement?.blur();
    setPhase("loader");

    const { firstName, lastName } = splitName(fullName);

    try {
      // 1) REGISTRA FIREBASE (gestito dal parent)
      await onRegister({ firstName, lastName, email, password });

      // 2) EMAILJS (non blocca il successo)
      emailjs
        .send(
          emailJs.service,
          emailJs.template,
          {
            firstName,
            lastName,
            email,
            product,
            message: S(
              ["popup.confirmMessage", "compareSection.popup.confirmMessage"],
              `Nuova richiesta: ${product}`
            ),
          },
          emailJs.publicKey
        )
        .catch((e2) => console.warn("EmailJS error:", e2));

      // 3) SUCCESS
      setPhase("success");
    } catch (errReg) {
      console.error("Registration error:", errReg);

      const msg =
        typeof errReg === "string"
          ? errReg
          : errReg?.message
          ? String(errReg.message)
          : S(
              ["compareSection.form.registerFailed", "form.registerFailed"],
              "Registrazione fallita. Riprova."
            );

      setErr(msg);
      setPhase("form");
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[2147483000] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/80" />

      <div className="relative z-[2147483001] bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-md shadow-2xl">
        {phase === "form" && (
          <>
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 text-slate-400 hover:text-white"
              aria-label="Chiudi"
            >
              <svg
                className="h-6 w-6"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18 18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div className="text-center mb-4">
              <h3 className="text-xl font-bold text-white">
                {S(["compareSection.popupTitle"], `Acquista ${product}`)}
              </h3>
              <p className="text-slate-400 text-sm">
                {S(
                  ["compareSection.popupSubtitle"],
                  "Inserisci i dati per registrarti e confermare"
                )}
              </p>
            </div>

            {err && (
              <div className="mb-3 text-xs text-red-300 bg-red-900/20 border border-red-700/40 rounded px-3 py-2">
                {err}
              </div>
            )}

            <form
              ref={formRef}
              onSubmit={submit}
              className="space-y-4"
              noValidate
            >
              <input type="hidden" name="product" value={product} />

              <div>
                <label className="block text-slate-300 text-xs mb-1">
                  {S(
                    ["compareSection.form.fullName", "compareSection.form.name"],
                    "Nome completo *"
                  )}
                </label>
                <input
                  name="fullName"
                  required
                  value={form.fullName}
                  onChange={handleChange}
                  className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder={S(
                    [
                      "compareSection.form.fullNamePlaceholder",
                      "compareSection.form.namePlaceholder",
                    ],
                    "Mario Rossi"
                  )}
                />
              </div>

              <div>
                <label className="block text-slate-300 text-xs mb-1">
                  {S(["compareSection.form.email"], "Email *")}
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  inputMode="email"
                  value={form.email}
                  onChange={handleChange}
                  className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder={S(
                    ["compareSection.form.emailPlaceholder"],
                    "you@email.com"
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-300 text-xs mb-1">
                    {S(["compareSection.form.password"], "Password *")}
                  </label>
                  <input
                    type="password"
                    name="password"
                    required
                    minLength={6}
                    value={form.password}
                    onChange={handleChange}
                    className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder={S(
                      ["compareSection.form.passwordPlaceholder"],
                      "Min 6 characters"
                    )}
                  />
                </div>

                <div>
                  <label className="block text-slate-300 text-xs mb-1">
                    {S(["compareSection.form.confirmPassword"], "Conferma *")}
                  </label>
                  <input
                    type="password"
                    name="confirm"
                    required
                    minLength={6}
                    value={form.confirm}
                    onChange={handleChange}
                    className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder={S(
                      ["compareSection.form.confirmPlaceholder"],
                      "Repeat password"
                    )}
                  />
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium py-2.5 rounded-lg shadow"
              >
                {S(["compareSection.form.submit"], "Register & Get Access")}
              </button>

              <div className="mt-2 text-center text-[11px] text-slate-500">
                {S(
                  ["compareSection.disclaimer"],
                  "Cliccando accetti i nostri termini."
                )}{" "}
                <a
                  href="/terms"
                  className="text-purple-400 hover:underline font-medium"
                >
                  {S(["compareSection.terms"], "Termini")}
                </a>
                {" & "}
                <a
                  href="/privacy"
                  className="text-purple-400 hover:underline font-medium"
                >
                  {S(["compareSection.privacy"], "Privacy")}
                </a>
                .
              </div>
            </form>
          </>
        )}

        {phase === "loader" && (
          <div className="flex flex-col items-center justify-center h-56">
            <h4 className="text-white font-semibold mb-6">
              {S(
                ["loader.title", "compareSection.loader.title"],
                "Validazione pagamento"
              )}
            </h4>
            <div className="flex items-center justify-center space-x-3">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="w-3.5 h-3.5 rounded-full bg-purple-400"
                  style={{ animation: `dotPulse 1s ${i * 0.15}s infinite` }}
                />
              ))}
            </div>
            <p className="text-slate-400 text-sm mt-4">
              {S(
                ["loader.subtitle", "compareSection.loader.subtitle"],
                "Attendere…"
              )}
            </p>
          </div>
        )}

        {phase === "success" && (
          <div className="text-center">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-5">
              <svg
                className="w-10 h-10 text-green-400"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            <h3 className="text-2xl font-extrabold text-white mb-2">
              {S(
                ["popup.successTitle", "compareSection.popup.successTitle"],
                "Acquisto confermato"
              )}
            </h3>

            <p className="text-slate-300 mb-2">
              {S(
                ["popup.successText", "compareSection.popup.successText"],
                "Email inviata a"
              )}{" "}
              <span className="text-white font-semibold">
                {form.email || "—"}
              </span>
              .
            </p>

            <p className="text-xs text-slate-400 mb-5">
              {S(
                ["popup.successNote", "compareSection.popup.successNote"],
                "Controlla anche lo spam."
              )}
            </p>

            <button
              type="button"
              onClick={onClose}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium py-2.5 rounded-lg shadow"
            >
              {S(["popup.close", "compareSection.popup.close"], "Chiudi")}
            </button>
          </div>
        )}
      </div>
    </div>,
    portalRoot
  );
}
